import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import apiService, { Translation, Language } from '../services/apiService';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { cn } from '../lib/utils';
import {
  Globe,
  Search,
  Filter,
  Edit3,
  Plus,
  Loader2,
  X,
  Zap,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const Translations: React.FC = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [translations, setTranslations] = useState<Translation[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<any>(null);

  // Filters
  const [selectedLanguage, setSelectedLanguage] = useState(searchParams.get('language') || '');
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'));

  // Edit modal
  const [editingTranslation, setEditingTranslation] = useState<Translation | null>(null);
  const [editingLanguage, setEditingLanguage] = useState<string>('');
  const [editedText, setEditedText] = useState('');
  const [saving, setSaving] = useState(false);
  const [tmSuggestions, setTmSuggestions] = useState<any>(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  useEffect(() => {
    loadLanguages();
  }, []);

  useEffect(() => {
    if (siteId) {
      loadTranslations();
    }
  }, [siteId, selectedLanguage, searchTerm, statusFilter, currentPage]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedLanguage) params.set('language', selectedLanguage);
    if (searchTerm) params.set('search', searchTerm);
    if (statusFilter) params.set('status', statusFilter);
    if (currentPage > 1) params.set('page', currentPage.toString());

    setSearchParams(params);
  }, [selectedLanguage, searchTerm, statusFilter, currentPage, setSearchParams]);

  const loadLanguages = async () => {
    try {
      const languageList = await apiService.getLanguages();
      setLanguages(languageList);
    } catch (error: any) {
      console.error('Failed to load languages:', error);
    }
  };

  const loadTranslations = async () => {
    if (!siteId) return;

    try {
      setLoading(true);
      const response = await apiService.getSiteTranslations(siteId, {
        language: selectedLanguage || undefined,
        search: searchTerm || undefined,
        status: statusFilter || undefined,
        page: currentPage,
        limit: 25
      });

      setTranslations(response.translations);
      setPagination(response.pagination);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = async (translation: Translation, language: string) => {
    setEditingTranslation(translation);
    setEditingLanguage(language);
    setEditedText(translation.translations[language] || '');

    if (siteId && translation.original_text) {
      setLoadingSuggestions(true);
      try {
        const suggestions = await apiService.getTranslationMemorySuggestions({
          siteId,
          sourceText: translation.original_text,
          sourceLanguage: 'en',
          targetLanguage: language
        });
        setTmSuggestions(suggestions);
      } catch (err) {
        console.error('Failed to load TM suggestions:', err);
        setTmSuggestions(null);
      } finally {
        setLoadingSuggestions(false);
      }
    }
  };

  const closeEditModal = () => {
    setEditingTranslation(null);
    setEditingLanguage('');
    setEditedText('');
    setTmSuggestions(null);
  };

  const saveTranslation = async () => {
    if (!editingTranslation) return;

    try {
      setSaving(true);

      await apiService.updateTranslation(editingTranslation.id, {
        translated_text: editedText,
        status: 'translated'
      });

      setTranslations(prev => prev.map(t =>
        t.id === editingTranslation.id
          ? {
              ...t,
              translations: {
                ...t.translations,
                [editingLanguage]: editedText
              },
              status: 'translated'
            }
          : t
      ));

      closeEditModal();
    } catch (error: any) {
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const getLanguageName = (code: string) => {
    const language = languages.find(l => l.code === code);
    return language ? language.native_name : code;
  };

  const getStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case 'translated':
        return 'default';
      case 'pending':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getQualityColor = (score: number) => {
    if (score >= 0.9) return 'bg-green-500';
    if (score >= 0.75) return 'bg-primary';
    if (score >= 0.6) return 'bg-yellow-500';
    return 'bg-destructive';
  };

  const getQualityTextColor = (score: number) => {
    if (score >= 0.9) return 'text-green-700';
    if (score >= 0.75) return 'text-primary';
    if (score >= 0.6) return 'text-yellow-700';
    return 'text-destructive';
  };

  if (loading && translations.length === 0) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center mb-6">
          <Skeleton className="h-7 w-32 mb-2" />
        </div>
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-0">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="p-6 border-b last:border-0">
                <Skeleton className="h-6 w-48 mb-2" />
                <Skeleton className="h-4 w-full mb-4" />
                <Skeleton className="h-4 w-32" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-foreground">Translations</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Manage translations for all content keys across different languages.
          </p>
        </div>
      </div>

      {error && (
        <Card className="mb-6 border-destructive/50 bg-destructive/10">
          <CardContent className="pt-4">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div className="space-y-2">
              <Label>Language</Label>
              <select
                value={selectedLanguage}
                onChange={(e) => {
                  setSelectedLanguage(e.target.value);
                  setCurrentPage(1);
                }}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">All Languages</option>
                {languages.map(lang => (
                  <option key={lang.code} value={lang.code}>
                    {lang.native_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="translated">Translated</option>
              </select>
            </div>

            <div className="sm:col-span-2 space-y-2">
              <Label>Search Content</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Search content keys or text..."
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Translations Table */}
      {translations.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Globe className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No translations found</h3>
            <p className="text-muted-foreground">
              {searchTerm || selectedLanguage || statusFilter
                ? 'Try adjusting your filters to see more results.'
                : 'Content will appear here once your website starts sending translation keys.'
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Content Key
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Original Text
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Translations
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {translations.map((translation) => (
                  <tr key={translation.id} className="hover:bg-muted/30">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">
                      <div className="max-w-xs truncate" title={translation.content_key}>
                        {translation.content_key}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-foreground">
                      <div className="max-w-md">
                        {translation.original_text}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-foreground">
                      <div className="space-y-3">
                        {Object.entries(translation.translations).map(([langCode, translatedText]) => {
                          const qualityScore = translation.quality_scores?.[langCode];
                          return (
                            <div key={langCode} className="flex items-center justify-between">
                              <div className="flex-1 flex items-center space-x-2">
                                <Badge variant="secondary">
                                  {getLanguageName(langCode)}
                                </Badge>
                                <span className="max-w-xs truncate" title={translatedText}>
                                  {translatedText}
                                </span>
                                {qualityScore !== undefined && (
                                  <div className="flex items-center space-x-1" title={`Quality Score: ${Math.round(qualityScore * 100)}%`}>
                                    <div className="w-16 h-2 bg-secondary rounded-full overflow-hidden">
                                      <div
                                        className={cn("h-full transition-all", getQualityColor(qualityScore))}
                                        style={{ width: `${qualityScore * 100}%` }}
                                      />
                                    </div>
                                    <span className={cn("text-xs font-medium", getQualityTextColor(qualityScore))}>
                                      {Math.round(qualityScore * 100)}%
                                    </span>
                                  </div>
                                )}
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditModal(translation, langCode)}
                              >
                                <Edit3 className="h-3 w-3 mr-1" />
                                Edit
                              </Button>
                            </div>
                          );
                        })}
                        {languages.filter(lang => !translation.translations[lang.code]).map(lang => (
                          <div key={lang.code} className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <Badge variant="outline">
                                {lang.native_name}
                              </Badge>
                              <span className="text-muted-foreground text-xs italic">Not translated</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditModal(translation, lang.code)}
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add
                            </Button>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant={getStatusVariant(translation.status)}>
                        {translation.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * pagination.items_per_page) + 1} to{' '}
            {Math.min(currentPage * pagination.items_per_page, pagination.total_items)} of{' '}
            {pagination.total_items} results
          </p>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>

            {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
              const page = i + Math.max(1, currentPage - 2);
              if (page > pagination.total_pages) return null;

              return (
                <Button
                  key={page}
                  variant={page === currentPage ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </Button>
              );
            })}

            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(pagination.total_pages, prev + 1))}
              disabled={currentPage === pagination.total_pages}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Edit Translation Modal */}
      {editingTranslation && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto py-10">
          <Card className="w-full max-w-4xl mx-4">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Edit Translation</CardTitle>
                <Button variant="ghost" size="icon" onClick={closeEditModal}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left side - Translation Form */}
                <div className="lg:col-span-2 space-y-4">
                  <div className="space-y-2">
                    <Label>Content Key</Label>
                    <div className="text-sm text-muted-foreground bg-muted p-3 rounded-md font-mono">
                      {editingTranslation.content_key}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Original Text</Label>
                    <div className="text-sm text-foreground bg-muted p-3 rounded-md border">
                      {editingTranslation.original_text}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Translation ({getLanguageName(editingLanguage)})</Label>
                    <Textarea
                      value={editedText}
                      onChange={(e) => setEditedText(e.target.value)}
                      rows={4}
                      placeholder="Enter translation..."
                    />
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <Button variant="outline" onClick={closeEditModal}>
                      Cancel
                    </Button>
                    <Button
                      onClick={saveTranslation}
                      disabled={saving || !editedText.trim()}
                    >
                      {saving ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save Translation'
                      )}
                    </Button>
                  </div>
                </div>

                {/* Right side - TM Suggestions */}
                <div className="lg:col-span-1 bg-muted/50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-foreground mb-3 flex items-center">
                    <Zap className="h-4 w-4 mr-2 text-primary" />
                    Translation Memory
                  </h4>

                  {loadingSuggestions ? (
                    <div className="text-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                      <p className="mt-2 text-xs text-muted-foreground">Loading suggestions...</p>
                    </div>
                  ) : tmSuggestions ? (
                    <div className="space-y-3">
                      {tmSuggestions.type === 'exact' && tmSuggestions.match && (
                        <Card className="border-green-200 bg-green-50">
                          <CardContent className="p-3">
                            <div className="flex items-center justify-between mb-2">
                              <Badge variant="default" className="bg-green-600">
                                100% Match
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                Used {tmSuggestions.match.useCount}x
                              </span>
                            </div>
                            <p className="text-sm text-foreground mb-3">{tmSuggestions.match.targetText}</p>
                            <Button
                              size="sm"
                              className="w-full bg-green-600 hover:bg-green-700"
                              onClick={() => setEditedText(tmSuggestions.match.targetText)}
                            >
                              Use This Translation
                            </Button>
                          </CardContent>
                        </Card>
                      )}

                      {tmSuggestions.type === 'fuzzy' && tmSuggestions.matches && tmSuggestions.matches.length > 0 && (
                        <>
                          {tmSuggestions.matches.map((match: any, idx: number) => {
                            const similarity = match.similarity * 100;
                            return (
                              <Card key={idx} className="border-primary/20">
                                <CardContent className="p-3">
                                  <div className="flex items-center justify-between mb-2">
                                    <Badge variant="secondary">
                                      {Math.round(similarity)}% Match
                                    </Badge>
                                    <span className="text-xs text-muted-foreground">
                                      Used {match.useCount}x
                                    </span>
                                  </div>
                                  <p className="text-xs text-muted-foreground mb-1">
                                    <span className="font-medium">Source:</span> {match.sourceText}
                                  </p>
                                  <p className="text-sm text-foreground mb-3">{match.targetText}</p>
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    className="w-full"
                                    onClick={() => setEditedText(match.targetText)}
                                  >
                                    Use This Translation
                                  </Button>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </>
                      )}

                      {tmSuggestions.type === 'none' && (
                        <div className="text-center py-8">
                          <Globe className="h-10 w-10 mx-auto text-muted-foreground" />
                          <p className="mt-2 text-xs text-muted-foreground">No matches found</p>
                          <p className="mt-1 text-xs text-muted-foreground">Your translation will be added to memory</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-xs text-muted-foreground">Translation Memory suggestions will appear here</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Translations;
